/**
 * Forgot Password Screen
 *
 * Initiates password reset by sending verification code to user's email/phone
 */

import { FormEvent, useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';

interface ForgotPasswordProps {
  identifier: string; // Pre-filled from login screen
  onSubmit: (identifier: string) => Promise<void>;
  error?: string | null;
}

export function ForgotPassword({
  identifier: initialIdentifier,
  onSubmit,
  error,
}: ForgotPasswordProps) {
  const [identifier, setIdentifier] = useState(initialIdentifier);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await onSubmit(identifier);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
        Forgot Password
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        We&apos;ll send a verification code to your email or phone number.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Email or Phone"
        value={identifier}
        onChange={(e) => setIdentifier(e.target.value)}
        margin="normal"
        required
        autoFocus
        disabled={isLoading}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isLoading || !identifier}
        sx={{ mt: 3, mb: 2 }}
      >
        {isLoading ? 'Sending...' : 'Send Reset Code'}
      </Button>
    </Box>
  );
}
